import { useEffect, useState, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useAnnouncementBar } from '@/hooks/useAnnouncementBar';
import type { AnimationEffect } from '@/hooks/useAnnouncementBar';
import { ChevronLeft, ChevronRight } from 'lucide-react';

const splitItems = (text: string) => text.split('•').map(s => s.trim()).filter(Boolean);

const textStyle = (color: string): React.CSSProperties => ({
  color,
  fontFamily: "'Oswald', sans-serif",
});

export const AnnouncementBar = () => {
  const { announcement, loading } = useAnnouncementBar();
  const location = useLocation();

  const isDashboardPage = location.pathname.startsWith('/dashboard') ||
    location.pathname.startsWith('/owner-dashboard') ||
    location.pathname.startsWith('/admin');

  const isVisible = !loading && announcement?.is_enabled && !!announcement?.text && !isDashboardPage;

  useEffect(() => {
    if (isVisible) {
      document.documentElement.classList.add('has-announcement');
    } else {
      document.documentElement.classList.remove('has-announcement');
    }
    return () => document.documentElement.classList.remove('has-announcement');
  }, [isVisible]);

  if (!isVisible || !announcement) return null;

  const effect = (announcement.animation_effect || 'scroll') as AnimationEffect;
  const speed = announcement.scroll_speed || 20;

  return (
    <div
      className="w-full overflow-hidden"
      style={{ backgroundColor: announcement.background_color }}
      id="announcement-bar"
    >
      {effect === 'scroll' && <ScrollEffect text={announcement.text} speed={speed} textColor={announcement.text_color} />}
      {effect === 'fade' && <FadeEffect text={announcement.text} speed={speed} textColor={announcement.text_color} />}
      {effect === 'slide-up' && <SlideUpEffect text={announcement.text} speed={speed} textColor={announcement.text_color} />}
      {effect === 'typewriter' && <TypewriterEffect text={announcement.text} speed={speed} textColor={announcement.text_color} />}
      {effect === 'none' && <StaticEffect text={announcement.text} textColor={announcement.text_color} />}
    </div>
  );
};

const ScrollEffect = ({ text, speed, textColor }: { text: string; speed: number; textColor: string }) => (
  <div
    className="inline-block py-2 whitespace-nowrap"
    style={{ animation: `marquee ${speed}s linear infinite` }}
  >
    <span className="text-sm font-medium uppercase tracking-wider" style={textStyle(textColor)}>
      {[0, 1, 2, 3].map((i) => (
        <span key={i} className="px-8">{text}</span>
      ))}
    </span>
  </div>
);

const useCycleIndex = (length: number, intervalMs: number) => {
  const [index, setIndex] = useState(0);

  const cycle = useCallback(() => {
    setIndex(prev => (prev + 1) % length);
  }, [length]);

  useEffect(() => {
    if (length <= 1) return;
    const id = setInterval(cycle, intervalMs);
    return () => clearInterval(id);
  }, [cycle, intervalMs, length]);

  return index;
};

const FadeEffect = ({ text, speed, textColor }: { text: string; speed: number; textColor: string }) => {
  const items = splitItems(text);
  const duration = Math.max(speed * 200, 2000);
  const index = useCycleIndex(items.length, duration);

  return (
    <div className="flex items-center justify-center py-2" style={{ minHeight: '2.25rem' }}>
      <span
        key={index}
        className="text-sm font-medium uppercase tracking-wider px-8"
        style={{ ...textStyle(textColor), animation: `announcement-fade ${duration}ms ease-in-out` }}
      >
        {items[index]}
      </span>
    </div>
  );
};

const SlideUpEffect = ({ text, speed, textColor }: { text: string; speed: number; textColor: string }) => {
  const items = splitItems(text);
  const duration = Math.max(speed * 200, 2000);
  const index = useCycleIndex(items.length, duration);

  return (
    <div className="flex items-center justify-center py-2 overflow-hidden" style={{ height: '2.25rem' }}>
      <span
        key={index}
        className="text-sm font-medium uppercase tracking-wider px-8"
        style={{ ...textStyle(textColor), animation: `announcement-slide-up ${duration}ms ease-in-out` }}
      >
        {items[index]}
      </span>
    </div>
  );
};

const TypewriterEffect = ({ text, speed, textColor }: { text: string; speed: number; textColor: string }) => {
  const items = splitItems(text);
  const typeDuration = Math.max(speed * 0.5, 2);
  const index = useCycleIndex(items.length, typeDuration * 1000 * 2);
  const currentItem = items[index];

  return (
    <div className="flex items-center justify-center py-2">
      <span
        key={index}
        className="text-sm font-medium uppercase tracking-wider px-8 overflow-hidden border-r-2 whitespace-nowrap"
        style={{
          ...textStyle(textColor),
          borderColor: textColor,
          animation: `announcement-typewriter ${typeDuration}s steps(${currentItem.length}, end) infinite alternate, blink-caret 0.75s step-end infinite`,
          maxWidth: `${currentItem.length + 2}ch`,
          display: 'inline-block',
        }}
      >
        {currentItem}
      </span>
    </div>
  );
};

const StaticEffect = ({ text, textColor }: { text: string; textColor: string }) => {
  const items = splitItems(text);
  const [index, setIndex] = useState(0);

  const prev = () => setIndex(i => (i - 1 + items.length) % items.length);
  const next = () => setIndex(i => (i + 1) % items.length);

  if (items.length <= 1) {
    return (
      <div className="flex items-center justify-center py-2">
        <span className="text-sm font-medium uppercase tracking-wider px-8" style={textStyle(textColor)}>
          {items[0]}
        </span>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center py-2 gap-2 relative">
      <button
        onClick={prev}
        className="p-0.5 rounded-full hover:opacity-70 transition-opacity shrink-0"
        style={{ color: textColor }}
        aria-label="Previous announcement"
      >
        <ChevronLeft className="w-4 h-4" />
      </button>
      <span
        key={index}
        className="text-sm font-medium uppercase tracking-wider px-4 animate-fade-in"
        style={textStyle(textColor)}
      >
        {items[index]}
      </span>
      <button
        onClick={next}
        className="p-0.5 rounded-full hover:opacity-70 transition-opacity shrink-0"
        style={{ color: textColor }}
        aria-label="Next announcement"
      >
        <ChevronRight className="w-4 h-4" />
      </button>
    </div>
  );
};
